import { useCallback, useEffect, useState } from "react";
import { describeError } from "../common/utils/error_message";

const parseClipboardValue = (rawStorageValue, parseErrorMessage) => {
	if (!rawStorageValue) return { value: null, isExpired: false };

	try {
		const parsed = JSON.parse(rawStorageValue);
		const expiresAt = parsed?.expiresAt;
		const value = parsed?.value;

		if (!expiresAt || !value) {
			return { value: null, isExpired: true };
		}

		if (Date.now() > expiresAt) {
			return { value: null, isExpired: true };
		}

		return { value, isExpired: false };
	} catch (error) {
		console.error(parseErrorMessage, describeError(error));
		return { value: null, isExpired: true };
	}
};

export const useStorageClipboard = ({
	storageKey,
	ttlMs,
	checkIntervalMs,
	parseErrorMessage,
	syncErrorMessage,
}) => {
	const [value, setValueState] = useState(() => {
		const savedClipboard = localStorage.getItem(storageKey);
		const { value: parsedValue, isExpired } = parseClipboardValue(
			savedClipboard,
			parseErrorMessage,
		);

		if (isExpired) {
			localStorage.removeItem(storageKey);
		}

		return parsedValue;
	});

	const setValue = useCallback(
		nextValueOrUpdater => {
			setValueState(prevValue => {
				const nextValue =
					typeof nextValueOrUpdater === "function"
						? nextValueOrUpdater(prevValue)
						: nextValueOrUpdater;

				try {
					if (nextValue) {
						const expiresAt = Date.now() + ttlMs;
						localStorage.setItem(
							storageKey,
							JSON.stringify({
								value: nextValue,
								expiresAt,
							}),
						);
					} else {
						localStorage.removeItem(storageKey);
					}
				} catch (error) {
					console.error(syncErrorMessage, describeError(error));
				}

				return nextValue;
			});
		},
		[storageKey, syncErrorMessage, ttlMs],
	);

	useEffect(() => {
		const handleStorage = event => {
			if (event.key !== storageKey) return;

			const { value: parsedValue, isExpired } = parseClipboardValue(
				event.newValue,
				parseErrorMessage,
			);

			if (isExpired) {
				localStorage.removeItem(storageKey);
			}

			setValueState(parsedValue);
		};

		window.addEventListener("storage", handleStorage);

		return () => {
			window.removeEventListener("storage", handleStorage);
		};
	}, [parseErrorMessage, storageKey]);

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			const savedClipboard = localStorage.getItem(storageKey);
			const { value: parsedValue, isExpired } = parseClipboardValue(
				savedClipboard,
				parseErrorMessage,
			);

			if (!isExpired) return;

			localStorage.removeItem(storageKey);
			setValueState(parsedValue);
		}, checkIntervalMs);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [checkIntervalMs, parseErrorMessage, storageKey]);

	return [value, setValue];
};
